// bug-capture.js
// Framework-free capture SDK for Kayana's self-rectifying bug agent (wave 1).
// Copied byte-identical into every consuming app — see
// docs/superpowers/specs/2026-08-27-self-rectifying-bug-agent-design.md §6.1.
// No bundler assumed: this file runs both as a CRA-imported ES module and as
// a plain `<script type="module">` include from a Laravel blade partial, so
// it never uses a bare `import` for anything that isn't guaranteed to also
// resolve unbundled (see captureScreenshot's window.html2canvas-first check).

let config = {
  app: null,
  apiBase: "",
  reportPath: "/report",
  listPath: "/list",
  getToken: () => null,
  appVersion: null,
  redactAttr: "data-bug-redact",
};

function init(options) {
  config = {
    app: options.app,
    apiBase: String(options.apiBase || "").replace(/\/$/, ""),
    reportPath: options.reportPath || "/report",
    // Not called internally yet — kept on config so a consuming app's own
    // list screen can build the same URL shape without hardcoding it twice.
    listPath: options.listPath || "/list",
    getToken: typeof options.getToken === "function" ? options.getToken : () => null,
    appVersion: options.appVersion || null,
    redactAttr: options.redactAttr || "data-bug-redact",
  };
  if (typeof window !== "undefined") {
    window.addEventListener("online", () => queueFlush().catch(() => {}));
    queueFlush().catch(() => {});
  }
}

// --- Fingerprinting ---------------------------------------------------

function normaliseRoute(route) {
  if (!route) return "";
  return String(route)
    .split("?")[0]
    .split("/")
    .map((segment) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
      /^\d+$/.test(segment)
        ? ":id"
        : segment
    )
    .join("/");
}

function normaliseMessage(message) {
  if (!message) return "";
  return String(message)
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ":uuid")
    .replace(/\d{4}-\d{2}-\d{2}T[\d:.Z+-]+/g, ":timestamp")
    .replace(/\b\d{10,13}\b/g, ":timestamp")
    .replace(/[?&][^=\s]+=[^&\s]+/g, "")
    .replace(/\b\d+\b/g, ":id");
}

function topFrame(stack) {
  if (!stack) return "";
  const lines = String(stack)
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.length > 1 ? lines[1] : lines[0] || "";
}

function rotl(value, shift) {
  return (value << shift) | (value >>> (32 - shift));
}

// Self-contained SHA-1 — no Web Crypto dependency. SubtleCrypto requires a
// secure context and is async; this keeps fingerprint() synchronous and
// usable from a plain, unbundled <script type="module"> on any origin.
function sha1Hex(input) {
  const utf8 = unescape(encodeURIComponent(input));
  const bytes = [];
  for (let i = 0; i < utf8.length; i++) bytes.push(utf8.charCodeAt(i));
  const bitLength = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  for (let i = 7; i >= 0; i--) bytes.push(Math.floor(bitLength / Math.pow(2, i * 8)) & 0xff);

  let h0 = 0x67452301, h1 = 0xefcdab89, h2 = 0x98badcfe, h3 = 0x10325476, h4 = 0xc3d2e1f0;

  for (let chunkStart = 0; chunkStart < bytes.length; chunkStart += 64) {
    const w = new Array(80);
    for (let i = 0; i < 16; i++) {
      w[i] =
        (bytes[chunkStart + i * 4] << 24) |
        (bytes[chunkStart + i * 4 + 1] << 16) |
        (bytes[chunkStart + i * 4 + 2] << 8) |
        bytes[chunkStart + i * 4 + 3];
    }
    for (let i = 16; i < 80; i++) {
      w[i] = rotl(w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1);
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4;
    for (let i = 0; i < 80; i++) {
      let f, k;
      if (i < 20) { f = (b & c) | (~b & d); k = 0x5a827999; }
      else if (i < 40) { f = b ^ c ^ d; k = 0x6ed9eba1; }
      else if (i < 60) { f = (b & c) | (b & d) | (c & d); k = 0x8f1bbcdc; }
      else { f = b ^ c ^ d; k = 0xca62c1d6; }
      const temp = (rotl(a, 5) + f + e + k + w[i]) >>> 0;
      e = d; d = c; c = rotl(b, 30); b = a; a = temp;
    }
    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0; h4 = (h4 + e) >>> 0;
  }

  return [h0, h1, h2, h3, h4].map((part) => (part >>> 0).toString(16).padStart(8, "0")).join("");
}

function fingerprint(parts) {
  const raw = [
    parts.app || "",
    parts.platform || "",
    normaliseRoute(parts.route),
    parts.kind || "",
    normaliseMessage(parts.message),
    topFrame(parts.stack),
  ].join("");
  return sha1Hex(raw);
}

// --- Scrubbing (matches EPOS PiiScrubber.kt) ---------------------------------
// Order matters: auth headers first (broadest), then bearer, then field names,
// then card PANs (with spaces/dashes), then email, then phone.

const _SCRUB_AUTH  = /Authorization\s*:\s*[^\n]*/gi;
const _SCRUB_BEARER = /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi;
const _SCRUB_FIELD  = /\b(cvv|cvc|pin|iban|sortCode|sort_code|accountNumber|account_number|pspRef|psp_ref|payoutRef|payout_ref|settlementRef|settlement_ref|wage|salary|nino|nationalInsurance|national_insurance|ssn)\b\s*[:=]\s*"?[^,"}\n]+/gi;
const _SCRUB_CARD   = /\b(?:\d[ -]?){12,18}\d\b/g;
const _SCRUB_EMAIL  = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const _SCRUB_PHONE  = /\+?\d[\d\s-]{7,}\d/g;

function scrub(text) {
  if (!text) return text;
  return String(text)
    .replace(_SCRUB_AUTH,   "Authorization: [REDACTED]")
    .replace(_SCRUB_BEARER, "Bearer [REDACTED]")
    .replace(_SCRUB_FIELD,  (m) => m.split(/[:=]/)[0].trim() + ": [REDACTED]")
    .replace(_SCRUB_CARD,   "[REDACTED_CARD]")
    .replace(_SCRUB_EMAIL,  "[REDACTED_EMAIL]")
    .replace(_SCRUB_PHONE,  "[REDACTED_PHONE]");
}

// consoleTail may arrive as a string or an array of lines.
function scrubConsoleTail(tail) {
  if (!tail) return null;
  return Array.isArray(tail) ? tail.map((l) => scrub(l)) : scrub(tail);
}

// --- Screenshot -----------------------------------------------------------

async function captureScreenshot(options = {}) {
  try {
    const renderer =
      (typeof window !== "undefined" && window.html2canvas) ||
      (await import(/* webpackChunkName: "html2canvas" */ "html2canvas")).default;
    const canvas = await renderer(document.body, {
      logging: false,
      useCORS: true,
      // Lets a caller skip its own UI chrome (e.g. a dialog that's already
      // painted over the page it wants captured) without touching the
      // PII-redaction hook below.
      ignoreElements: options.ignoreElements,
      onclone: (clonedDoc) => {
        clonedDoc.querySelectorAll(`[${config.redactAttr}]`).forEach((el) => {
          el.style.setProperty("background", "#000", "important");
          el.style.setProperty("color", "#000", "important");
          Array.from(el.querySelectorAll("*")).forEach((child) => {
            child.style.setProperty("visibility", "hidden", "important");
          });
        });
      },
    });
    return await new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.6));
  } catch (error) {
    return null;
  }
}

// --- Headers helper -------------------------------------------------------

function buildHeaders() {
  const headers = { "Content-Type": "application/json" };
  const token = config.getToken();
  if (token) headers.Authorization = "Bearer " + String(token).replace(/^Bearer\s+/i, "");
  return headers;
}

// --- Offline queue (matches EPOS OfflineQueue.kt) -------------------------
// localStorage-backed, bounded to 20 reports. Screenshots not queued —
// screenshotPutUrl upload is best-effort and handled server-side anyway.

const _QUEUE_KEY = "kayana_bug_queue";
const _QUEUE_MAX = 20;

function _queueLoad() {
  try { return JSON.parse(localStorage.getItem(_QUEUE_KEY) || "[]"); } catch { return []; }
}

function _queueSave(items) {
  try { localStorage.setItem(_QUEUE_KEY, JSON.stringify(items)); } catch { /* storage full — drop */ }
}

function _queueEnqueue(body) {
  const items = _queueLoad();
  items.push({ body, ts: Date.now() });
  if (items.length > _QUEUE_MAX) items.splice(0, items.length - _QUEUE_MAX);
  _queueSave(items);
}

async function queueFlush() {
  if (typeof localStorage === "undefined") return;
  const items = _queueLoad();
  if (!items.length) return;
  const remaining = [];
  await Promise.all(
    items.map((item) =>
      fetch(config.apiBase + config.reportPath, {
        method: "POST",
        headers: buildHeaders(),
        credentials: "same-origin",
        body: JSON.stringify(item.body),
      })
        .then((r) => { if (!r.ok) remaining.push(item); })
        .catch(() => remaining.push(item))
    )
  );
  _queueSave(remaining);
}

// --- Noise control ----------------------------------------------------

const REPEAT_WINDOW_MS = 60 * 1000;
const RATE_CAP_PER_MINUTE = 5;
const lastSeenByFingerprint = new Map();
const recentSendTimestamps = [];

function allowSend(fp) {
  const now = Date.now();
  const last = lastSeenByFingerprint.get(fp);
  if (last && now - last < REPEAT_WINDOW_MS) return false;
  while (recentSendTimestamps.length && now - recentSendTimestamps[0] > REPEAT_WINDOW_MS) {
    recentSendTimestamps.shift();
  }
  if (recentSendTimestamps.length >= RATE_CAP_PER_MINUTE) return false;
  lastSeenByFingerprint.set(fp, now);
  recentSendTimestamps.push(now);
  return true;
}

// --- Report -----------------------------------------------------------------

async function report(input) {
  const platform = "WEB";
  const kind = input.kind || "JS_ERROR";
  const route = input.route || (typeof window !== "undefined" ? window.location.pathname : null);
  // Allow caller to pass an explicit fingerprint (e.g. chat replies reusing the same bug row).
  const fp = input.fingerprint || fingerprint({ app: config.app, platform, route, kind, message: input.message, stack: input.stack });

  // Skip repeat/rate-cap check for MANUAL chat turns — they are user-driven and intentional.
  if (kind !== "MANUAL" && !input.fingerprint && !allowSend(fp)) {
    return Promise.reject(new Error("bug-capture: report suppressed (repeat or rate cap)"));
  }

  const message = scrub(input.message || "");
  const body = {
    schemaVersion: 1,
    platform,
    tier: input.tier || "C",
    kind,
    fingerprint: fp,
    summary: message.slice(0, 500),
    message,
    stack: input.stack ? scrub(input.stack) : null,
    fileLine: input.fileLine || null,
    route: route || null,
    occurredAt: new Date().toISOString(),
    userNote: input.userNote ? scrub(input.userNote) : null,
    wantsScreenshot: !!input.wantsScreenshot,
    screenshotBase64: input.screenshotBase64 || null,
    context: {
      url: typeof window !== "undefined" ? window.location.pathname : null,
      method: (input.context && input.context.method) || null,
      status: (input.context && input.context.status) || null,
      requestBody:
        input.context && input.context.requestBody ? scrub(JSON.stringify(input.context.requestBody)) : null,
      responseBody:
        input.context && input.context.responseBody ? scrub(JSON.stringify(input.context.responseBody)) : null,
      consoleTail: scrubConsoleTail(input.context && input.context.consoleTail),
      appVersion: config.appVersion,
      osVersion: null,
      deviceId: null,
      networkState: "ONLINE",
    },
  };

  const response = await fetch(config.apiBase + config.reportPath, {
    method: "POST",
    headers: buildHeaders(),
    credentials: "same-origin",
    body: JSON.stringify(body),
  }).catch(() => null);

  if (!response || !response.ok) {
    _queueEnqueue(body);
    throw new Error(`bug-capture: report POST failed${response ? ` (${response.status})` : " (network)"}`);
  }
  const responseJson = await response.json();
  // Some backends (e.g. business-service) wrap every response in a
  // {status, message, data: {...}} envelope; others return the payload
  // unwrapped. Unwrap when present so screenshotPutUrl/id/etc. are found
  // either way, without needing a per-backend fork of this file.
  const data =
    responseJson && typeof responseJson.data === "object" && responseJson.data !== null
      ? responseJson.data
      : responseJson;
  // Fall back to top-level message field for botReply (validation rejection path).
  const botReply = data.botReply || responseJson.message || null;

  if (input.wantsScreenshot && data.screenshotPutUrl) {
    captureScreenshot()
      .then((blob) => {
        if (!blob) return null;
        return fetch(data.screenshotPutUrl, {
          method: "PUT",
          headers: { "Content-Type": "image/jpeg" },
          body: blob,
        });
      })
      .catch(() => {
        // An upload failure must never fail the report — spec §7.1.
      });
  }

  return { id: data.id, isNew: data.isNew, occurrences: data.occurrences, fingerprint: data.fingerprint || fp, botReply };
}

const bugCapture = { init, report };
export default bugCapture;
export { fingerprint, scrub, captureScreenshot };
